import styled from 'styled-components';
import type { OrganizationDetailContentLayout } from './types';

export const DetailContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    background: var(--color-background, #fff);
`;

export const TopBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 16px;
    border-bottom: 1px solid var(--color-border, #e5e7eb);
    background: var(--color-background, white);
    flex-shrink: 0;
`;

export const TopBarLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

export const TopBarTitle = styled.h1`
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--color-foreground, #111827);
    line-height: 1.3;
`;

export const TopBarSubtitle = styled.p`
    margin: 0;
    font-size: 12px;
    color: var(--color-muted-foreground, #6b7280);
`;

export const TabList = styled.div`
    display: flex;
    gap: 2px;
    padding: 0 16px;
    border-bottom: 1px solid var(--color-border, #e5e7eb);
    background: var(--color-background, white);
    flex-shrink: 0;
`;

export const TabButton = styled.button<{ $isActive: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    font-size: 13px;
    font-weight: 500;
    border: none;
    background: none;
    cursor: pointer;
    border-bottom: 2px solid
        ${({ $isActive }) => ($isActive ? 'var(--color-primary, #2563eb)' : 'transparent')};
    color: ${({ $isActive }) =>
        $isActive ? 'var(--color-primary, #2563eb)' : 'var(--color-muted-foreground, #6b7280)'};
    transition:
        color 0.15s ease,
        border-color 0.15s ease;

    svg {
        width: 13px;
        height: 13px;
        flex-shrink: 0;
    }

    &:hover {
        color: ${({ $isActive }) =>
            $isActive ? 'var(--color-primary, #2563eb)' : 'var(--color-foreground, #111827)'};
    }
`;

export const ContentScroll = styled.div<{ $layout: OrganizationDetailContentLayout }>`
    flex: 1;
    min-height: 0;
    overflow: ${({ $layout }) => ($layout === 'services' ? 'hidden' : 'auto')};
    padding: ${({ $layout }) => ($layout === 'services' ? '0' : '12px 16px 16px')};
`;

export const PanelCard = styled.section`
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: var(--radius-md, 12px);
    padding: var(--space-4, 16px);
    background: var(--color-background, #fff);
`;

export const PanelTitle = styled.h2`
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-muted-foreground, #6b7280);
`;

export const PanelLede = styled.p`
    margin: 0 0 14px;
    font-size: var(--font-size-sm, 13px);
    color: var(--color-muted-foreground, #6b7280);
`;

export const StatGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;

    @media (max-width: 800px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

export const StatCard = styled.div`
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: var(--radius-sm, 10px);
    padding: 12px;
    background: var(--color-muted, #fafbfc);
`;

export const StatLabel = styled.span`
    display: block;
    font-size: 12px;
    color: var(--color-muted-foreground, #6b7280);
    margin-bottom: 4px;
`;

export const StatValue = styled.span`
    font-size: 18px;
    font-weight: 650;
    letter-spacing: -0.02em;
    color: var(--color-foreground, #111827);
`;

export const MetaRow = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 14px;

    @media (max-width: 800px) {
        grid-template-columns: 1fr;
    }
`;

export const MetaItem = styled.div`
    font-size: 13px;
`;

export const MetaLabel = styled.span`
    display: block;
    color: var(--color-muted-foreground, #6b7280);
    font-size: 12px;
    margin-bottom: 2px;
`;

export const MetaCode = styled.code`
    font-family: var(--font-mono, ui-monospace, monospace);
    font-size: 12px;
`;

export const CycleWrap = styled.div`
    margin-top: 14px;
`;

export const CycleLabelRow = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: var(--color-muted-foreground, #6b7280);
    margin-bottom: 6px;
`;

export const CycleTrack = styled.div`
    height: 8px;
    border-radius: 999px;
    background: var(--color-border, #e2e8f0);
    overflow: hidden;
`;

export const CycleFill = styled.div<{ $percent: number }>`
    height: 100%;
    width: ${({ $percent }) => `${$percent}%`};
    background: linear-gradient(90deg, #0f766e, #14b8a6);
`;
